import { useEffect, useState } from 'react';
import {
  Paper,
  Toolbar,
  Stack,
  IconButton,
  Typography,
  Box,
  Divider,
  Button,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { LoadingButton } from '@mui/lab';
import { ArrowBack } from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router';
import LoadingMask from '@/components/shared/LoadingMask';
import ErrorSnackBar from '@/components/shared/ErrorSnackBar';
import { getManifest, splitManifest } from '@/actions/manifest';
import { useApi } from '@/providers/ApiContext';
import ManifestNameField from '../AdminManifestForm/ManifestNameField';
import { useUser } from '@/providers/UserContext';
import { useDocTypes } from '@/providers/DocTypesContext';
import { getManifestChecks } from '@/actions/manifestCheck';
import ManifestCheckTransferList from './ManifestCheckTransferList';
import type { Manifest, ManifestCheck } from '@/types';

export default function AdminSplitManifestForm(): React.JSX.Element {
  const { manifest_id } = useParams<{ manifest_id: string }>();
  const api = useApi();
  const user = useUser();
  const doc_types = useDocTypes();
  const navigate = useNavigate();

  const [revision, setRevision] = useState<number>(0);
  const [pristine, setPristine] = useState<boolean>(true);
  const [loading, setLoading] = useState<boolean>(false);
  const [loading_manifest, setLoadingManifest] = useState<boolean>(true);
  const [loading_checks, setLoadingChecks] = useState<boolean>(true);
  const [manifest, setManifest] = useState<Manifest | null>(null);
  const [name, setName] = useState<string>('');
  const [name_error, setNameError] = useState<string | boolean>(false);
  const [manifest_checks, setManifestChecks] = useState<ManifestCheck[] | null>(
    null,
  );
  const [split_checks, setSplitChecks] = useState<ManifestCheck[]>([]);
  const [api_error, setApiError] = useState<string | null>(null);

  useEffect(() => {
    if (user?.entity_id && !manifest && manifest_id && api) {
      getManifest(api, manifest_id)
        .then((data) => {
          setManifest(data);
          setLoadingManifest(false);
        })
        .catch((error) => {
          console.log(error);
          setLoadingManifest(false);
          navigate('/error', {
            state: {
              errorMessage: 'Failed to load manifest for splitting',
              errorDetails:
                (error as Error).message ||
                'Unable to retrieve the manifest for splitting. This could be due to network issues, server problems, or the manifest may no longer exist.',
              returnPath: `/admin/manifests/${manifest_id}`,
            },
          });
        });
    }
  }, [api, user?.entity_id, manifest, manifest_id, navigate]);

  useEffect(() => {
    setRevision((prevState) => prevState + 1);
  }, [manifest, doc_types]);

  useEffect(() => {
    if (
      user?.entity_id &&
      manifest?.entity_id &&
      !manifest_checks &&
      manifest_id &&
      api
    ) {
      getManifestChecks(api, manifest.entity_id, 100)
        .then((data) => {
          setManifestChecks(
            data.results.sort((a: ManifestCheck, b: ManifestCheck) => {
              const aSection = a.section || '';
              const bSection = b.section || '';
              if (aSection === bSection) {
                return a.name > b.name ? 1 : -1;
              } else {
                if (aSection === 'global') {
                  return -1;
                } else if (bSection === 'global') {
                  return 1;
                } else {
                  return aSection > bSection ? 1 : -1;
                }
              }
            }),
          );
          setLoadingChecks(false);
        })
        .catch((error) => {
          console.log(error);
          setLoadingChecks(false);
          navigate('/error', {
            state: {
              errorMessage: 'Failed to load manifest checks',
              errorDetails:
                (error as Error).message ||
                'Unable to retrieve the manifest checks. This could be due to network issues, server problems, or the manifest may be in an invalid state.',
              returnPath: `/admin/manifests/${manifest_id}`,
            },
          });
        });
    }
  }, [
    user?.entity_id,
    manifest?.entity_id,
    manifest_checks,
    api,
    manifest_id,
    navigate,
  ]);

  const handleSubmit = async (): Promise<void> => {
    if (!manifest_id || !manifest || !split_checks.length || !api) return;
    setLoading(true);
    const payload: Record<string, unknown> = {
      name,
      checks: split_checks.map((check) => check.entity_id),
    };
    console.log(payload);
    try {
      await splitManifest(api, manifest_id, payload);

      if (doc_types) {
        const doc_type = doc_types.find(
          (doc_type) => doc_type.entity_id === manifest.document_type_id,
        );
        if (doc_type) {
          navigate({
            pathname: '/admin/manifests',
            search: `?type=${doc_type.code}`,
          });
        }
      }
    } catch (error) {
      console.error('Error splitting manifest:', error);
      const err = error as Error;
      setApiError(
        err.message ||
          'Unable to split the manifest. This could be due to network issues, server problems, or invalid data.',
      );
      setLoading(false);
    }
  };

  if (loading_manifest || loading_checks) {
    return <LoadingMask />;
  }

  return (
    <Grid container spacing={2}>
      <Grid size={12}>
        <Paper sx={{ display: 'flex', width: '100%' }} elevation={2}>
          <Toolbar
            sx={{
              minHeight: '55px !important',
            }}
          >
            <Stack spacing={2} direction='row' alignItems='center'>
              <IconButton onClick={() => navigate(-1)} disabled={loading}>
                <ArrowBack />
              </IconButton>
              <Typography variant='h5'>{`Split ${manifest?.name || ''}`}</Typography>
            </Stack>
          </Toolbar>
        </Paper>
      </Grid>

      <Grid size={12}>
        <Box
          padding='15px'
          sx={{
            maxWidth: 600,
          }}
        >
          <Grid container spacing={2}>
            <Grid size={12}>
              <ManifestNameField
                key={`name_${revision}`}
                value={name}
                error={name_error}
                setValue={setName}
                setError={setNameError}
                setChanged={() => setPristine(false)}
                label={'New Manifest Name'}
              />
            </Grid>
          </Grid>
        </Box>
      </Grid>
      <Grid size={12}>
        <ManifestCheckTransferList
          key={`checks_${revision}`}
          source_checks={manifest_checks || []}
          setSplitChecks={setSplitChecks}
        />
      </Grid>

      <Grid size={12}>
        <Divider />
        <Toolbar>
          <Stack spacing={2} direction='row'>
            <LoadingButton
              variant='contained'
              onClick={handleSubmit}
              loading={loading}
              disabled={
                pristine ||
                Boolean(name_error) ||
                !Boolean(name) ||
                !split_checks.length
              }
            >
              Submit
            </LoadingButton>

            <Button
              variant='outlined'
              onClick={() => navigate(-1)}
              disabled={loading}
            >
              Cancel
            </Button>
          </Stack>
        </Toolbar>
      </Grid>

      <ErrorSnackBar message={api_error} onClear={() => setApiError(null)} />
    </Grid>
  );
}
